/**
 * EntityPermissionManager.js
 *
 * Generic, reusable permission management UI for any entity type.
 * Integrates with the entity_permissions framework via the chatbot_router
 * permission CRUD API endpoints.
 *
 * Usage:
 *   <EntityPermissionManager
 *     entityType="chatbot_workflow"      // or "cdp_session"
 *     entityId={workflowId}
 *     entityName="My Workflow"           // display name
 *     apiPrefix="/api/chatbot"           // or "/api/cdp"
 *     isOpen={showPermissions}
 *     onClose={() => setShowPermissions(false)}
 *   />
 *
 * API endpoints used (prefix varies by entity):
 *   GET    {apiPrefix}/permissions/{entityType}/{entityId}
 *   POST   {apiPrefix}/permissions/{entityType}/{entityId}
 *   POST   {apiPrefix}/permissions/{entityType}/{entityId}/batch
 *   DELETE {apiPrefix}/permissions/{entityType}/{entityId}/{username}
 *   GET    {apiPrefix}/users/search?q=...
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Users, UserPlus, Shield, ShieldCheck, ShieldAlert, Eye, Pencil, Crown,
  Headphones, Trash2, Search, Clock, ChevronDown, AlertCircle, Check, RefreshCw,
  Copy, UserMinus
} from 'lucide-react';
import { apiCall } from './Api';
import { getCurrentToken } from './SessionManager';

// ==================== Constants ====================

const PERMISSION_LEVELS = [
  { value: 'view', label: 'View', icon: Eye, color: 'text-blue-600 bg-blue-50 border-blue-200', desc: 'Read-only access to data and analytics' },
  { value: 'operate', label: 'Operate', icon: Headphones, color: 'text-amber-600 bg-amber-50 border-amber-200', desc: 'View + perform actions (handoff, send messages)' },
  { value: 'edit', label: 'Edit', icon: Pencil, color: 'text-green-600 bg-green-50 border-green-200', desc: 'Operate + modify settings and content' },
  { value: 'admin', label: 'Admin', icon: Crown, color: 'text-purple-600 bg-purple-50 border-purple-200', desc: 'Full control including delete and permissions' },
];

const ENTITY_LABELS = {
  chatbot_workflow: { singular: 'Workflow', plural: 'Workflows', icon: '&#x2699;' },
  cdp_session: { singular: 'CDP Session', plural: 'CDP Sessions', icon: '&#x1F4CA;' },
};

// ==================== Helpers ====================

const authHeaders = () => {
  const token = getCurrentToken();
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const permLevel = (perm) => PERMISSION_LEVELS.find(p => p.value === perm) || PERMISSION_LEVELS[0];

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// ==================== Permission Badge ====================

const PermissionBadge = ({ permission, size = 'sm' }) => {
  const level = permLevel(permission);
  const Icon = level.icon;
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-medium ${level.color} ${sizeClasses}`}>
      <Icon className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
      {level.label}
    </span>
  );
};

// ==================== Permission Selector Dropdown ====================

const PermissionSelector = ({ value, onChange, compact = false }) => {
  const [open, setOpen] = useState(false);
  const btnRef = React.useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const current = permLevel(value);
  const Icon = current.icon;

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - 256), // 256 = w-64, keep on screen
      });
    }
    setOpen(!open);
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={handleOpen}
        className={`inline-flex items-center gap-1.5 rounded-lg border font-medium transition-colors hover:bg-gray-50 ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} ${current.color}`}
      >
        <Icon className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        {current.label}
        <ChevronDown className="h-3 w-3 opacity-50" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div
            className="fixed bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 z-[61] w-64"
            style={{ top: pos.top, left: pos.left }}
          >
            {PERMISSION_LEVELS.map(level => {
              const LIcon = level.icon;
              const selected = value === level.value;
              return (
                <button
                  key={level.value}
                  onClick={() => { onChange(level.value); setOpen(false); }}
                  className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 flex items-start gap-3 transition-colors ${selected ? 'bg-gray-50' : ''}`}
                >
                  <div className={`mt-0.5 p-1 rounded-md ${level.color}`}>
                    <LIcon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{level.label}</span>
                      {selected && <Check className="h-3.5 w-3.5 text-blue-600" />}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{level.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// ==================== Kind + Role Badges ====================

const KindBadge = ({ kind }) => {
  if (kind === 'app') {
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-cyan-100 text-cyan-700 border border-cyan-200">
        APP
      </span>
    );
  }
  return null;
};

const RoleBadge = ({ role, kind }) => {
  const colors = {
    admin: 'bg-purple-100 text-purple-700',
    host_admin: 'bg-indigo-100 text-indigo-700',
    host_user: 'bg-gray-100 text-gray-600',
    host_agent: 'bg-amber-100 text-amber-700',
    host_customer: 'bg-green-100 text-green-700',
    host_app: 'bg-cyan-100 text-cyan-700',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[role] || 'bg-gray-100 text-gray-500'}`}>
      {kind === 'app' ? 'app' : role}
    </span>
  );
};

// ==================== User Search Input ====================

const UserSearchInput = ({ selectedUsers, onSelect, onRemove, existingUsernames = [], apiPrefix }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [kindFilter, setKindFilter] = useState('all'); // 'all', 'users', 'apps'
  const inputRef = React.useRef(null);
  const debounceRef = React.useRef(null);

  const searchUsers = useCallback(async (q, kind) => {
    setLoading(true);
    try {
      const res = await apiCall(`${apiPrefix}/users/search?q=${encodeURIComponent(q)}&kind=${kind}&limit=15`, {
        headers: authHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data.users || []);
      }
    } catch (err) {
      console.error('User search failed:', err);
    }
    setLoading(false);
  }, [apiPrefix]);

  const handleFocus = () => {
    setFocused(true);
    if (results.length === 0 && !query) {
      searchUsers('', kindFilter);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!focused) return;
    debounceRef.current = setTimeout(() => {
      searchUsers(query, kindFilter);
    }, query ? 250 : 0);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, kindFilter, focused, searchUsers]);

  const alreadySelected = new Set([
    ...selectedUsers.map(u => u.username),
    ...existingUsernames,
  ]);

  const filteredResults = results.filter(u => !alreadySelected.has(u.username));

  return (
    <div className="relative">
      {/* Selected chips */}
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {selectedUsers.map(u => (
          <span key={u.username} className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs border ${
            u.kind === 'app' ? 'bg-cyan-50 border-cyan-200 text-cyan-800' : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <span className="font-medium">{u.kind === 'app' ? (u.display_name || u.username) : u.username}</span>
            <RoleBadge role={u.role} kind={u.kind} />
            <button onClick={() => onRemove(u.username)} className="ml-0.5 p-0.5 hover:bg-blue-100 rounded-full">
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
      </div>

      {/* Kind filter tabs */}
      <div className="flex items-center gap-1 mb-2">
        {[
          { value: 'all', label: 'All' },
          { value: 'users', label: 'Users' },
          { value: 'apps', label: 'Apps' },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setKindFilter(tab.value)}
            className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
              kindFilter === tab.value
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          placeholder={kindFilter === 'apps' ? 'Search applications...' : kindFilter === 'users' ? 'Search users...' : 'Search users & apps...'}
          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        {loading && (
          <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Dropdown results */}
      {focused && (results.length > 0 || loading) && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 max-h-52 overflow-y-auto z-50">
          {filteredResults.length === 0 && !loading ? (
            <div className="px-4 py-3 text-xs text-gray-400 text-center">
              {results.length > 0 ? 'All matching results already added' : 'No results found'}
            </div>
          ) : (
            filteredResults.map(user => (
              <button
                key={user.username}
                onMouseDown={(e) => { e.preventDefault(); onSelect(user); setQuery(''); }}
                className="w-full px-3 py-2.5 text-left hover:bg-blue-50 flex items-center gap-3 transition-colors"
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  user.kind === 'app' ? 'bg-cyan-100' : 'bg-gray-200'
                }`}>
                  <span className={`text-[10px] font-bold uppercase ${
                    user.kind === 'app' ? 'text-cyan-700' : 'text-gray-600'
                  }`}>{user.kind === 'app' ? '\u26A1' : user.username.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-gray-900">
                      {user.kind === 'app' ? (user.display_name || user.username) : user.username}
                    </span>
                    {user.kind === 'app' && <KindBadge kind="app" />}
                  </div>
                  {user.kind === 'app' && user.domain && (
                    <p className="text-[11px] text-gray-400 truncate">{user.domain}</p>
                  )}
                  {user.kind === 'app' && (
                    <p className="text-[10px] text-gray-400 font-mono">{user.username}</p>
                  )}
                </div>
                <RoleBadge role={user.role} kind={user.kind} />
              </button>
            ))
          )}
          {loading && filteredResults.length === 0 && (
            <div className="px-4 py-3 text-xs text-gray-400 text-center flex items-center justify-center gap-2">
              <RefreshCw className="h-3 w-3 animate-spin" /> Searching...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== Add User Form ====================

const AddUserForm = ({ entityType, entityId, onGranted, existingUsernames = [], apiPrefix }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [permission, setPermission] = useState('view');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSelect = (user) => {
    if (!selectedUsers.find(u => u.username === user.username)) {
      setSelectedUsers(prev => [...prev, user]);
    }
  };

  const handleRemove = (username) => {
    setSelectedUsers(prev => prev.filter(u => u.username !== username));
  };

  const handleGrant = async () => {
    if (selectedUsers.length === 0) { setError('Select at least one user'); return; }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const body = {
        permission,
        ...(expiresAt ? { expires_at: new Date(expiresAt).toISOString() } : {}),
      };

      let res;
      if (selectedUsers.length === 1) {
        body.username = selectedUsers[0].username;
        res = await apiCall(`${apiPrefix}/permissions/${entityType}/${entityId}`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify(body)
        });
      } else {
        body.usernames = selectedUsers.map(u => u.username);
        res = await apiCall(`${apiPrefix}/permissions/${entityType}/${entityId}/batch`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify(body)
        });
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${res.status})`);
      }

      const count = selectedUsers.length;
      setSuccess(`Granted ${permission} to ${count} user${count > 1 ? 's' : ''}`);
      setSelectedUsers([]);
      setExpiresAt('');
      onGranted();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="border border-dashed border-gray-300 rounded-xl p-4 bg-gray-50/50">
      <div className="flex items-center gap-2 mb-3">
        <UserPlus className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">Grant Access</span>
      </div>

      <div className="space-y-3">
        <UserSearchInput
          selectedUsers={selectedUsers}
          onSelect={handleSelect}
          onRemove={handleRemove}
          existingUsernames={existingUsernames}
          apiPrefix={apiPrefix}
        />

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Permission:</span>
            <PermissionSelector value={permission} onChange={setPermission} compact />
          </div>

          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-gray-400" />
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-blue-500"
              title="Optional: expires at"
            />
            {expiresAt && (
              <button onClick={() => setExpiresAt('')} className="text-gray-400 hover:text-gray-600">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          <button
            onClick={handleGrant}
            disabled={submitting || selectedUsers.length === 0}
            className="ml-auto flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
            {submitting ? 'Granting...' : `Grant${selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ''}`}
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 rounded-lg px-3 py-2">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 text-green-600 text-xs bg-green-50 rounded-lg px-3 py-2">
            <Check className="h-3.5 w-3.5 flex-shrink-0" /> {success}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Grant Row ====================

const GrantRow = ({ grant, entityType, entityId, onRevoked, onUpdated, apiPrefix }) => {
  const [revoking, setRevoking] = useState(false);
  const [updating, setUpdating] = useState(false);
  const isExpired = grant.expires_at && new Date(grant.expires_at) < new Date();

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      const res = await apiCall(
        `${apiPrefix}/permissions/${entityType}/${entityId}/${encodeURIComponent(grant.username)}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      if (!res.ok) throw new Error('Failed to revoke');
      onRevoked(grant.username);
    } catch (err) {
      console.error(err);
    }
    setRevoking(false);
  };

  const handlePermissionChange = async (newPerm) => {
    setUpdating(true);
    try {
      const res = await apiCall(`${apiPrefix}/permissions/${entityType}/${entityId}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          username: grant.username,
          permission: newPerm,
          ...(grant.expires_at ? { expires_at: grant.expires_at } : {})
        })
      });
      if (!res.ok) throw new Error('Failed to update');
      onUpdated();
    } catch (err) {
      console.error(err);
    }
    setUpdating(false);
  };

  const isApp = grant.username.startsWith('app:');

  return (
    <div className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group ${isExpired ? 'opacity-50' : ''}`}>
      {/* Avatar + username */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isApp ? 'bg-cyan-100' : 'bg-gray-200'}`}>
        <span className={`text-xs font-bold uppercase ${isApp ? 'text-cyan-700' : 'text-gray-600'}`}>
          {isApp ? '⚡' : grant.username.charAt(0)}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 truncate">{grant.username}</span>
          {isApp && <KindBadge kind="app" />}
          {isExpired && (
            <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full font-medium">Expired</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-gray-400">
          <span>by {grant.granted_by}</span>
          <span>{timeAgo(grant.created_at)}</span>
          {grant.expires_at && !isExpired && (
            <span className="flex items-center gap-0.5">
              <Clock className="h-2.5 w-2.5" />
              expires {new Date(grant.expires_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* Permission selector */}
      <div className={updating ? 'opacity-50 pointer-events-none' : ''}>
        <PermissionSelector value={grant.permission} onChange={handlePermissionChange} compact />
      </div>

      {/* Revoke */}
      <button
        onClick={handleRevoke}
        disabled={revoking}
        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
        title="Revoke access"
      >
        {revoking ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <UserMinus className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
};

// ==================== Main Component ====================

const EntityPermissionManager = ({
  entityType,
  entityId,
  entityName,
  apiPrefix = '/api/chatbot',
  isOpen,
  onClose,
}) => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterPerm, setFilterPerm] = useState('');

  const entityLabel = ENTITY_LABELS[entityType]?.singular || entityType;

  const loadGrants = useCallback(async () => {
    if (!entityId || !isOpen) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiCall(`${apiPrefix}/permissions/${entityType}/${entityId}`, {
        headers: authHeaders()
      });
      if (!res.ok) {
        if (res.status === 403) { setError('You need admin permission to manage access'); }
        else { throw new Error(`Failed (${res.status})`); }
        setGrants([]);
      } else {
        const data = await res.json();
        setGrants(data.permissions || []);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [entityType, entityId, isOpen]);

  useEffect(() => { loadGrants(); }, [loadGrants]);

  if (!isOpen) return null;

  const filtered = grants.filter(g => {
    if (search && !g.username.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterPerm && g.permission !== filterPerm) return false;
    return true;
  });

  // Stats
  const stats = PERMISSION_LEVELS.map(l => ({
    ...l,
    count: grants.filter(g => g.permission === l.value).length
  }));

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel — slides from right */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Manage Access</h2>
                <p className="text-xs text-gray-500 mt-0.5 truncate max-w-72">
                  {entityLabel}: <span className="font-medium text-gray-700">{entityName || entityId}</span>
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Stats row */}
          {grants.length > 0 && (
            <div className="flex items-center gap-2 mt-3">
              {stats.filter(s => s.count > 0).map(s => (
                <button
                  key={s.value}
                  onClick={() => setFilterPerm(filterPerm === s.value ? '' : s.value)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-colors ${
                    filterPerm === s.value ? s.color + ' ring-2 ring-offset-1 ring-blue-300' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <s.icon className="h-3 w-3" />
                  {s.count} {s.label}
                </button>
              ))}
              <span className="text-xs text-gray-400 ml-auto">{grants.length} total</span>
            </div>
          )}
        </div>

        {/* Add user form */}
        <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <AddUserForm entityType={entityType} entityId={entityId} onGranted={loadGrants} existingUsernames={grants.map(g => g.username)} apiPrefix={apiPrefix} />
        </div>

        {/* Search */}
        {grants.length > 4 && (
          <div className="px-6 py-2 border-b border-gray-100 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by username..."
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Grant list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-6 w-6 text-blue-500 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <ShieldAlert className="h-10 w-10 text-red-300 mb-3" />
              <p className="text-sm text-red-600 font-medium">{error}</p>
              <button onClick={loadGrants} className="mt-3 text-xs text-blue-600 hover:underline">Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <Users className="h-10 w-10 text-gray-200 mb-3" />
              <p className="text-sm text-gray-500 font-medium">
                {grants.length === 0 ? 'No one else has access yet' : 'No matching users'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {grants.length === 0 ? 'Use the form above to share this ' + entityLabel.toLowerCase() : 'Try a different search'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map(grant => (
                <GrantRow
                  key={grant.id}
                  grant={grant}
                  entityType={entityType}
                  entityId={entityId}
                  onRevoked={(username) => setGrants(prev => prev.filter(g => g.username !== username))}
                  onUpdated={loadGrants}
                  apiPrefix={apiPrefix}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-gray-400 leading-relaxed">
              The {entityLabel.toLowerCase()} creator and admin/host_admin roles always have full access. Grant permissions to users or applications here.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export { EntityPermissionManager, PermissionBadge };
export default EntityPermissionManager;
